import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { CircularProgress, Typography } from '@material-ui/core';

import useStyles from '../useStyles';

const TRANSLATE_3D = /translate3d\(([^,]+),([^,]+),[^)]*\)/;

const Slide = ({ src, previewSrc, load, errorText }) => {
  const classes = useStyles();
  const zoomRef = useRef(null);
  const [status, setStatus] = useState('pending');
  const [previewFailed, setPreviewFailed] = useState(false);

  // Swiper Zoom's translate3d puts the photo on a GPU layer that Chrome draws with tile seams
  useEffect(() => {
    const observer = new MutationObserver((records) =>
      records.forEach(({ target }) => {
        const transform = target.style.transform.replace(TRANSLATE_3D, 'translate($1,$2)');
        if (transform !== target.style.transform) target.style.transform = transform;
      })
    );
    observer.observe(zoomRef.current, { attributes: true, attributeFilter: ['style'], subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!load || status !== 'pending') return;
    const original = new Image();
    original.onload = () => setStatus('loaded');
    original.onerror = () => setStatus('failed');
    original.src = src;
    return () => {
      original.onload = null;
      original.onerror = null;
    };
  }, [load, status, src]);

  const preview = !previewFailed && previewSrc;
  const shown = (load && status === 'loaded' && src) || preview || undefined;

  return (
    <>
      {/* one <img> whose src is swapped in place: Zoom keeps a reference to the element */}
      <div ref={zoomRef} className="swiper-zoom-container">
        <img
          src={shown}
          alt=""
          draggable={false}
          className={classnames(classes.image, !shown && classes.hidden)}
          onError={() => (shown === src ? setStatus('failed') : setPreviewFailed(true))}
        />
      </div>
      {!shown && (
        <div className={classes.status}>
          {status === 'failed' ? <Typography>{errorText}</Typography> : <CircularProgress color="inherit" />}
        </div>
      )}
    </>
  );
};

Slide.propTypes = {
  src: PropTypes.string.isRequired,
  previewSrc: PropTypes.string,
  load: PropTypes.bool.isRequired,
  errorText: PropTypes.string.isRequired,
};

Slide.defaultProps = {
  previewSrc: null,
};

export default Slide;
